import {Card, Segment, Trend} from 'platform/components';
import {
  Align,
  Box,
  Heading,
  Hide,
  Show,
  Space,
  Text,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {Trans} from 'react-i18next';

import {isNil, isNotNil, sort} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {Alert} from '../../../../components/Alert/Alert';
import {AvoidBreakInside} from '../../../../components/AvoidBreakInside/AvoidBreakInside';
import {PriceDistributionChart} from '../../../../components/PriceDistributionChart/PriceDistributionChart';
import i18n from '../../../../i18n/i18n';
import {MarketType} from '../../../../types/MarketType';
import {PriceReportType} from '../../../../types/PriceReportType';
import {ResponsiveGrid} from '../../components/ResponsiveGrid';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {getDiffLabel} from '../../utils/getDiffLabel';
import {getValueOrEmptyString} from '../../utils/getValueOrEmptyString';
import {ActualPriceChart} from './components/ActualPriceChart';
import {AveragePriceChart} from './components/AveragePriceChart';
import {getStatisticVariant} from './utils/getStatisticVariant';

type PriceView = 'position' | 'distribution';

const DEFAULT_PRICE_VIEW: PriceView = 'position';

interface StatisticsCardPriceProps extends TestIdProps {
  market?: MarketType;
  priceReport: PriceReportType;
}

export function StatisticsCardPrice(props: StatisticsCardPriceProps) {
  const {isPrint} = useIsPrintForced();
  const formatCurrency = useFormatCurrency();
  const variant = getStatisticVariant(
    props.priceReport.sellingPrice?.recommended?.value ?? 0,
    props.market?.sold?.averagePrice?.value ?? 0
  );

  const alertTranslationKey = match(variant)
    .with('success', () => 'entity.overviewStatistics.labels.priceAlertSuccess')
    .with('neutral', () => 'entity.overviewStatistics.labels.priceAlertNeutral')
    .with('warning', () => 'entity.overviewStatistics.labels.priceAlertWarning')
    .exhaustive();

  const alertIcon = match<string | undefined, ThemeIconKey | undefined>(variant)
    .with('success', () => 'action/trending_up')
    .with('warning', () => 'action/trending_down')
    .otherwise(() => undefined);

  const currency = props.priceReport?.sellingPrice?.recommended?.currency;
  const recommended = props.priceReport?.sellingPrice?.recommended?.value;
  const sold = props.market?.sold?.averagePrice?.value;
  const stock = props.market?.stock?.averagePrice?.value;

  const isSomeVehicleSold = (props.market?.sold?.similarVehicle ?? 0) > 0;
  const isSomeVehicleInStock = (props.market?.stock?.similarVehicle ?? 0) > 0;

  const averagePriceSold = isSomeVehicleSold ? props.market?.sold?.averagePrice : undefined;
  const averagePriceStock = isSomeVehicleInStock ? props.market?.stock?.averagePrice : undefined;

  const averageSoldDifferent =
    isNil(recommended) || isNil(sold) || !isSomeVehicleSold ? undefined : recommended - sold;
  const averageStockDifferent =
    isNil(recommended) || isNil(stock) || !isSomeVehicleInStock ? undefined : recommended - stock;

  const priceChartData = props.market?.priceChart?.original_price_with_vat;
  const chartData = isNotNilOrEmpty(priceChartData)
    ? sort(
        (a, b) => a - b,
        buildArray<number>(priceChartData).when(
          isNotNil(props.priceReport.sellingPrice?.recommended?.value),
          props.priceReport?.sellingPrice?.recommended?.value ?? 0
        )
      )
    : undefined;

  // The distribution view reuses the same comparable listings but keeps the
  // recommended price out of the sample (it is drawn as the marker instead).
  // With too few distinct prices the view renders its own empty state, so the
  // switcher stays available whenever any comparable listing exists at all.
  const canShowDistribution = isNotNilOrEmpty(priceChartData);
  const [priceView, setPriceView] = useState<PriceView>(DEFAULT_PRICE_VIEW);
  // Print has no interaction — always fall back to the default (position) view.
  const activePriceView: PriceView = isPrint ? DEFAULT_PRICE_VIEW : priceView;

  const priceViewOptions = [
    {value: 'position', label: i18n.t('entity.overviewChart.labels.priceViewPosition')},
    {value: 'distribution', label: i18n.t('entity.overviewChart.labels.priceViewDistribution')},
  ] satisfies {value: PriceView; label: string}[];

  return (
    <AvoidBreakInside>
      <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
        <Box padding={2}>
          <VStack spacing={6}>
            <Align center>
              <Heading size={3} data-testid={suffixTestId('priceTitle', props)}>
                {i18n.t('entity.overviewStatistics.labels.priceTitle')}
              </Heading>
            </Align>
            <Text
              align="center"
              size="small"
              color="secondary"
              data-testid={suffixTestId('priceDescription', props)}
            >
              <Trans
                i18nKey="entity.overviewStatistics.labels.priceDesciption"
                components={{bold: <Text key="bold" alternative inline size="small" />}}
              />
            </Text>
            <ResponsiveGrid>
              <Box>
                <Align center>
                  <Text
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId('recommendedVehiclePrice', props)}
                  >
                    {i18n.t('entity.overviewStatistics.labels.recommendedVehiclePrice')}
                  </Text>
                  <Space vertical={2} />
                  <Heading size={2} data-testid={suffixTestId('recommendedSellingPrice', props)}>
                    {getValueOrEmptyString(
                      props.priceReport.sellingPrice?.recommended,
                      (recommended) => formatCurrency(recommended.value, recommended.currency)
                    )}
                  </Heading>
                </Align>
              </Box>
              <Box>
                <VStack spacing={2} align="center">
                  <Align center>
                    <Text
                      size="xSmall"
                      color="secondary"
                      data-testid={suffixTestId('averageVehiclePrice', props)}
                    >
                      {i18n.t('entity.overviewStatistics.labels.averageVehiclePrice')}
                    </Text>
                    <Space vertical={2} />
                    <Heading size={2} data-testid={suffixTestId('soldAveragePrice', props)}>
                      {getValueOrEmptyString(averagePriceSold, (averagePrice) =>
                        formatCurrency(averagePrice.value, averagePrice.unit)
                      )}
                    </Heading>
                  </Align>
                  {isNotNil(averageSoldDifferent) && isNotNil(currency) && (
                    <Trend
                      label={getDiffLabel(averageSoldDifferent, (value) =>
                        formatCurrency(value, currency)
                      )}
                      isSubtle
                      isSmall
                      isPositive={averageSoldDifferent > 0}
                      isNegative={averageSoldDifferent < 0}
                      data-testid={suffixTestId('averageSoldDifferenceTrend', props)}
                    />
                  )}
                </VStack>
              </Box>
              <Box>
                <VStack align="center" spacing={2}>
                  <Align center>
                    <Text
                      size="xSmall"
                      color="secondary"
                      data-testid={suffixTestId('stockVehiclePrice', props)}
                    >
                      {i18n.t('entity.overviewStatistics.labels.stockVehiclePrice')}
                    </Text>
                    <Space vertical={2} />
                    <Heading size={2} data-testid={suffixTestId('stockAveragePrice', props)}>
                      {getValueOrEmptyString(averagePriceStock, (averagePrice) =>
                        formatCurrency(averagePrice.value, averagePrice.unit)
                      )}
                    </Heading>
                  </Align>
                  {isNotNil(averageStockDifferent) && isNotNil(currency) && (
                    <Trend
                      label={getDiffLabel(averageStockDifferent, (value) =>
                        formatCurrency(value, currency)
                      )}
                      isSubtle
                      isSmall
                      isPositive={averageStockDifferent > 0}
                      isNegative={averageStockDifferent < 0}
                      data-testid={suffixTestId('averageStockDifferenceTrend', props)}
                    />
                  )}
                </VStack>
              </Box>
            </ResponsiveGrid>
            <Hide
              when={
                isNil(props.priceReport.sellingPrice?.recommended?.value) ||
                isNil(props.market?.sold?.averagePrice?.value) ||
                !isSomeVehicleSold
              }
            >
              <Alert
                variant={variant === 'warning' ? 'warning' : 'success'}
                icon={alertIcon}
                data-testid={suffixTestId('priceAlert', props)}
              >
                <Trans
                  i18nKey={alertTranslationKey}
                  components={{bold: <Text key="bold" alternative inline size="small" />}}
                />
              </Alert>
            </Hide>
            {/* Switcher above the price chart: position among listings ↔ distribution */}
            <Show when={canShowDistribution && !isPrint}>
              <Align center>
                <Segment
                  value={activePriceView}
                  options={priceViewOptions}
                  onChange={setPriceView}
                  data-testid={suffixTestId('priceViewSwitch', props)}
                />
              </Align>
            </Show>
            {match(activePriceView)
              .with('position', () => (
                <ActualPriceChart
                  chartData={chartData}
                  averagePrice={props.market?.sold?.averagePrice}
                  vehiclePrice={props.priceReport.sellingPrice?.recommended?.value}
                  data-testid={suffixTestId('actualPriceChart', props)}
                />
              ))
              .with('distribution', () => (
                <PriceDistributionChart
                  priceReport={props.priceReport}
                  prices={priceChartData}
                  myPrice={props.priceReport.sellingPrice?.recommended?.value}
                  data-testid={suffixTestId('priceDistributionChart', props)}
                />
              ))
              .exhaustive()}
            <AveragePriceChart
              averageSoldPrice={props.market?.averagePriceChart?.sold}
              averageStockPrice={props.market?.averagePriceChart?.stock}
              period={props.market?.averagePriceChart?.period}
              data-testid={suffixTestId('averagePriceChart', props)}
            />
          </VStack>
        </Box>
      </Card>
    </AvoidBreakInside>
  );
}
